import { InternalStat, InternalVariable, INTERNAL_VARIABLES } from '@app/types/common';
import { ForecastModel } from '@app/common/static-data/models';
import { ForecastService } from '@app/api/forecasting';
import { JuaForecastRequest, JuaForecastResponse } from '@app/api/forecasting/types';

import { PlottableForecast } from '../types';

function kelvinToCelsius(kelvin: number): number {
  return kelvin - 273.15;
}

function addHoursToDate(date: Date, hours: number): Date {
  return new Date(new Date(date).setUTCHours(date.getUTCHours() + hours));
}

function toPlottableForecast({ forecast }: JuaForecastResponse): PlottableForecast {
  const { init_time, lead_times, times, points } = forecast;
  // Assume single point for now
  // TODO: think about multiple points
  const { returned_latlon, stats, ...variables } = points[0];
  const data: PlottableForecast['data'] = {};
  const statsData: Partial<Record<InternalVariable, Partial<Record<InternalStat, number[]>>>> = {};
  const { lat: latitude, lon: longitude } = returned_latlon;

  Object.entries(variables).forEach(([key, value]) => {
    if (INTERNAL_VARIABLES.includes(key as InternalVariable)) {
      data[key as InternalVariable] = value as number[];
    }
  });

  // Process stats if available
  if (stats) {
    Object.entries(stats).forEach(([variable, variableStats]) => {
      if (INTERNAL_VARIABLES.includes(variable as InternalVariable) && variableStats) {
        // Initialize the stats object for this variable if it doesn't exist
        if (!statsData[variable as InternalVariable]) {
          statsData[variable as InternalVariable] = {};
        }

        // Process all available stats for this variable
        Object.entries(variableStats).forEach(([statName, statValues]) => {
          if (statValues && Array.isArray(statValues)) {
            (statsData[variable as InternalVariable]! as any)[statName] = statValues;
          }
        });
      }
    });
  }

  // TODO: Remove this once the API returns TZ-aware datetimes
  const strToUTC = (timeStr: string): Date =>
    timeStr.includes('Z') ? new Date(timeStr) : new Date(timeStr + 'Z');

  const initTime = strToUTC(init_time);

  // TODO: Remove lead_time logic once the API returns `times`
  let time: Date[];
  if (times) {
    time = times.map(strToUTC);
  } else if (lead_times) {
    time = lead_times.map((leadTime) => {
      return addHoursToDate(initTime, leadTime);
    });
  } else {
    throw Error('Response should include `lead_times` or `times`');
  }

  data['air_temperature_at_height_level_2m'] =
    data['air_temperature_at_height_level_2m']?.map(kelvinToCelsius);

  data['dew_point_temperature_at_height_level_2m'] =
    data['dew_point_temperature_at_height_level_2m']?.map(kelvinToCelsius);

  // Scale relative humidity from 0-1 to 0-100%
  const relativeHumidityVariables = ['relative_humidity_at_height_level_2m'];
  relativeHumidityVariables.forEach((variable) => {
    if (data[variable as InternalVariable]) {
      data[variable as InternalVariable] = data[variable as InternalVariable]!.map(
        (val: number) => val * 100,
      );
    }
  });

  // Scale cloud cover variables from 0-1 to 0-100%
  const cloudCoverVariables = [
    'cloud_area_fraction_at_entire_atmosphere_high_type',
    'cloud_area_fraction_at_entire_atmosphere_medium_type',
    'cloud_area_fraction_at_entire_atmosphere_low_type',
  ];
  cloudCoverVariables.forEach((variable) => {
    if (data[variable as InternalVariable]) {
      data[variable as InternalVariable] = data[variable as InternalVariable]!.map(
        (val: number) => val * 100,
      );
    }
  });

  // Also convert temperature stats if they exist
  if (statsData['air_temperature_at_height_level_2m']) {
    const tempStats = statsData['air_temperature_at_height_level_2m']!;
    Object.keys(tempStats).forEach((statName) => {
      const statKey = statName as InternalStat;
      if (tempStats[statKey] && Array.isArray(tempStats[statKey])) {
        tempStats[statKey] = tempStats[statKey]!.map(kelvinToCelsius);
      }
    });
  }
  if (statsData['dew_point_temperature_at_height_level_2m']) {
    const tempStats = statsData['dew_point_temperature_at_height_level_2m']!;
    Object.keys(tempStats).forEach((statName) => {
      const statKey = statName as InternalStat;
      if (tempStats[statKey] && Array.isArray(tempStats[statKey])) {
        tempStats[statKey] = tempStats[statKey]!.map(kelvinToCelsius);
      }
    });
  }

  // Also scale cloud cover stats if they exist
  cloudCoverVariables.forEach((variable) => {
    if (statsData[variable as InternalVariable]) {
      const cloudStats = statsData[variable as InternalVariable]!;
      Object.keys(cloudStats).forEach((statName) => {
        const statKey = statName as InternalStat;
        if (cloudStats[statKey] && Array.isArray(cloudStats[statKey])) {
          cloudStats[statKey] = cloudStats[statKey]!.map((val: number) => val * 100);
        }
      });
    }
  });

  return {
    latitude,
    longitude,
    time,
    data,
    stats:
      Object.keys(statsData).length > 0 ? (statsData as PlottableForecast['stats']) : undefined,
  };
}

export async function getJuaForecast(
  modelId: ForecastModel,
  request: JuaForecastRequest,
): Promise<PlottableForecast> {
  const { initTime } = request;
  const response =
    initTime && initTime !== 'live' && initTime !== 'latest-full'
      ? await ForecastService.getInitTimeAtPoint(
          modelId,
          initTime,
          request.points[0].lat,
          request.points[0].lon,
          request.min_lead_time,
          request.max_lead_time,
          request.ensemble_stats,
          request.demo,
        )
      : await ForecastService.getLatestAtPoint(
          modelId,
          request.points[0].lat,
          request.points[0].lon,
          request.min_lead_time,
          request.max_lead_time,
          request.full,
          request.ensemble_stats,
          request.demo,
        );
  return toPlottableForecast(response);
}
